import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, Loader2, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/** P6.2 · Wishlist page — lists everything the current user has starred. */
export default function WishlistPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/wishlist");
      setRows(r.data.items || []);
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("Please log in."); nav("/"); return; }
      toast.error("Couldn't load wishlist");
    } finally { setBusy(false); }
  }, [nav]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/wishlist/${id}`);
      setRows((r) => r.filter((x) => x.wishlist_id !== id));
      toast.success("Removed");
    } catch { toast.error("Couldn't remove"); }
  };

  const targetLink = (r) => {
    if (r.object_type === "auction") return `/live-world/auction/${r.object_id}`;
    if (r.object_type === "order")   return `/transactions/order/${r.object_id}`;
    if (r.object_type === "need" || r.object_type === "intent") return `/live-world?focus=${r.object_id}`;
    if (r.object_type === "listing") return `/listing/${r.object_id}`;
    return `/live-world?focus=${r.object_id}`;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-stone-50 pb-20" data-testid="wishlist-page">
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-stone-200">
        <div className="max-w-2xl mx-auto px-3 py-2 flex items-center gap-2">
          <button onClick={() => nav(-1)} className="p-2 -ml-1 rounded-full hover:bg-stone-100" data-testid="wishlist-back">
            <ArrowLeft size={18}/>
          </button>
          <Heart size={16} className="text-rose-600"/>
          <div className="font-black text-stone-900">My Wishlist</div>
          <div className="ml-auto text-[11px] text-stone-500 font-bold" data-testid="wishlist-count">
            {rows.length}
          </div>
        </div>
      </div>
      <div className="max-w-2xl mx-auto p-3 space-y-2">
        {busy && rows.length === 0 && (
          <div className="py-16 text-center text-stone-500">
            <Loader2 size={20} className="animate-spin inline-block mr-1.5"/> Loading…
          </div>
        )}
        {!busy && rows.length === 0 && (
          <div className="py-16 text-center text-stone-500 text-sm" data-testid="wishlist-empty">
            Nothing saved yet. Tap the heart on any item to save it here.
          </div>
        )}
        {rows.map((r) => (
          <div key={r.wishlist_id}
               className="w-full text-left bg-white border border-stone-200 rounded-2xl p-3 flex items-center gap-3"
               data-testid={`wishlist-row-${r.wishlist_id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase font-black text-stone-500">{r.object_type}</div>
              <a href={targetLink(r)} className="font-black text-stone-900 text-sm truncate block">
                {r.object_id}
              </a>
              {r.note && <div className="text-xs text-stone-500 mt-0.5 truncate">{r.note}</div>}
            </div>
            <button onClick={() => remove(r.wishlist_id)}
                    className="p-2 rounded-full text-rose-600 hover:bg-rose-50"
                    data-testid={`wishlist-remove-${r.wishlist_id}`}>
              <Trash2 size={14}/>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
